// ユーザー一覧の表示(Show).
import React, { FC } from 'react';
import _ from 'lodash';

import * as UserListField from '../../constants/UserListField';
import { DATE_DISPLAY_TYPE } from '../../constants/Training';
import { SERVICE_CODE, ADL_CODE, SERVICE_CODE_SUPPORT } from '../../constants/csvCreate';

import GeneralPrimaryButton from '../Atoms/GeneralPrimaryButton';
import GeneralPrimarySelectBox from '../Atoms/GeneralPrimarySelectBox';
import DateSelectorWithPicker from '../Molecules/DateSelectorWithPicker';
import UserList from '../Molecules/UserListShow';

import TabSelector from '../Atoms/TabSelector';
// import MultiSelectBox from '../Atoms/MultiSelectBox';

import { makeStyles } from '@material-ui/core/styles';
import { Box, MenuItem, CircularProgress } from '@material-ui/core';
import { CSV_OUTPUT_ITEMS } from '../../constants/csvCreate';
// import { osJudgement } from '../../utils/commonUtil';

const useStyles = makeStyles(() => ({
  mainBody: {
    background: '#fff',
    borderRadius: '10px',
    '-moz-box-shadow': '0 1px 2px 1px rgba(0, 0, 0, 0.3)',
    '-webkit-box-shadow': '0 1px 2px 1px rgba(0, 0, 0, 0.3)',
    boxShadow: '0 1px 2px 1px rgba(0, 0, 0, 0.3)',
    '@media print': {
      boxShadow: 'none',
    },
  },
  headerTitle: {
    padding: '25px 30px 25px 20px',
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#fff',
    backgroundColor: '#7c644e',
    borderRadius: '10px 10px 0 0',
    '@media print': {
      display: 'none',
    },
  },
  ttlStyle01: {
    padding: '0 45px 0 5px',
    margin: '0 0 20px 0',
    fontSize: '25px',
    borderBottom: 'solid 1px #dcdcdc',
  },
  footerArea: {
    transform: 'translate(-50%, -50%)',
    '-webkit-transform': 'translate(-50%, -50%)',
    '-ms-transform': 'translate(-50%, -50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
}));
export interface LifeCSVOutputProps {
  location: any;
  refUsersList: any;
  minusMonthly: (className: string) => void;
  plusMonthly: (className: string) => void;
  allUsersPrintButtonClickHandler: () => void;
  date: moment.Moment;
  loaded: boolean;
  cachedUsers: UserListField.User[];
  toggleFunctionalTrainingPlan: boolean;
  toggleLifeCheck: boolean;
  toggleInterestCheck: boolean;
  toggleAdl: boolean;
  toggleScientificNursing: boolean;
  toggleUserInfo: boolean;
  serviceCode: SERVICE_CODE;
  serviceCodeSupport: SERVICE_CODE_SUPPORT;
  adlCode: ADL_CODE;
  switchToggleLifeCheck: (toggle: boolean) => void;
  switchToggleInterestCheck: (toggle: boolean) => void;
  switchToggleFunctionalTrainingPlan: (toggle: boolean) => void;
  switchToggleAdl: (toggle: boolean) => void;
  switchToggleScientificNursing: (toggle: boolean) => void;
  switchToggleUserInfo: (toggle: boolean) => void;
  outputItemNames: CSV_OUTPUT_ITEMS['name'][];
  handleCheckBoxClick: (event: React.ChangeEvent<{ value: unknown }>) => void;
  selectBoxChanged: (value: React.ChangeEvent<{ value: unknown }>) => void;
  selectCodeSupportBoxChanged: (value: React.ChangeEvent<{ value: unknown }>) => void;
  selectADLBoxChanged: (value: React.ChangeEvent<{ value: unknown }>) => void;
  onDateChange?: (newDate: moment.Moment) => void;
  onCheckChange?: (checkedUsers: UserListField.User[]) => void;
  usersWithData?: { [key: string]: boolean }; // データを持つユーザーのマップ
  isDataFetching?: boolean; // データ取得中かどうか
  basicInfoUsers?: string[]; // 利用者基本情報に登録されているユーザーIDのリスト
}

const LifeCSVOutput: FC<LifeCSVOutputProps> = ({
  location,
  refUsersList,
  minusMonthly,
  plusMonthly,
  allUsersPrintButtonClickHandler,
  date,
  loaded,
  cachedUsers,
  toggleInterestCheck,
  toggleLifeCheck,
  toggleFunctionalTrainingPlan,
  toggleAdl,
  toggleScientificNursing,
  toggleUserInfo,
  serviceCode,
  serviceCodeSupport,
  adlCode,
  switchToggleInterestCheck,
  switchToggleLifeCheck,
  switchToggleFunctionalTrainingPlan,
  switchToggleAdl,
  switchToggleScientificNursing,
  switchToggleUserInfo,
  outputItemNames,
  handleCheckBoxClick,
  selectBoxChanged,
  selectCodeSupportBoxChanged,
  selectADLBoxChanged,
  onDateChange,
  onCheckChange,
  usersWithData = {},
  isDataFetching = false,
  basicInfoUsers = [],
}) => {
  // チェックされたユーザーの数を管理するための状態変数
  const [checkedUsersCount, setCheckedUsersCount] = React.useState(0);

  // チェックボックスの状態が変更されたときに呼び出される関数
  const handleCheckChange = (checkedUsers: UserListField.User[]) => {
    setCheckedUsersCount(checkedUsers.length);
    // 親コンポーネントに通知
    if (onCheckChange) {
      onCheckChange(checkedUsers);
    }
  };
  const classes = useStyles();

  // 選択されているタブのインデックスを取得する関数
  const getSelectedTabIndex = () => {
    if (toggleFunctionalTrainingPlan) return 0;
    if (toggleLifeCheck) return 1;
    if (toggleInterestCheck) return 2;
    if (toggleScientificNursing) return 3;
    if (toggleAdl) return 4;
    if (toggleUserInfo) return 5;
    return 1; // デフォルトは「生活機能」タブ
  };

  // タブが変更されたときの処理
  const handleTabChange = (index: number) => {
    // 現在選択されているタブと同じタブがクリックされた場合は何もしない
    if (getSelectedTabIndex() === index) {
      return;
    }

    // すべてのトグルをオフにする
    switchToggleLifeCheck(false);
    switchToggleInterestCheck(false);
    switchToggleFunctionalTrainingPlan(false);
    switchToggleAdl(false);
    switchToggleUserInfo(false);
    switchToggleScientificNursing(false);

    // 選択されたタブに対応するトグルをオンにする
    switch (index) {
      case 0:
        switchToggleFunctionalTrainingPlan(true);
        break;
      case 1:
        switchToggleLifeCheck(true);
        break;
      case 2:
        switchToggleInterestCheck(true);
        break;
      case 3:
        switchToggleScientificNursing(true);
        break;
      case 4:
        switchToggleAdl(true);
        break;
      case 5:
        switchToggleUserInfo(true);
        break;
      default:
        break;
    }

    // 選択されたタブをローカルストレージに保存
    localStorage.setItem('selectedTabIndex', index.toString());
  };

  // コンポーネントのマウント時に、ローカルストレージから選択されていたタブを復元
  React.useEffect(() => {
    const savedTabIndex = localStorage.getItem('selectedTabIndex');
    if (savedTabIndex !== null) {
      const index = parseInt(savedTabIndex, 10);
      handleTabChange(index);
    }
  }, []);

  // タブの選択が利用者情報の場合のみ全ての利用者情報を表示
  // それ以外のタブの場合は、データ取得できた利用者のみを表示
  const displayUsers = React.useMemo(() => {
    if (toggleUserInfo) {
      // 利用者情報タブの場合は全ての利用者を表示
      return cachedUsers;
    } else {
      // それ以外のタブの場合は、データを持つ利用者のみを表示
      return cachedUsers.filter((user: UserListField.User) => usersWithData[user.user_id]);
    }
  }, [cachedUsers, toggleUserInfo, usersWithData]);

  // 表示するユーザーがいない場合、チェックされたユーザー数を0にリセット
  React.useEffect(() => {
    if (displayUsers.length === 0) {
      setCheckedUsersCount(0);
    }
  }, [displayUsers]);

  return (
    <>
      <Box
        component="article"
        position="relative"
        pb={{ xs: '230px', sm: '200px', md: '200px', lg: '150px' }}
        className={classes.mainBody}
      >
        <Box
          width="100%"
          height={{ xs: '230px', sm: '150px', md: '150px', lg: '150px' }}
          position="fixed"
          bottom={{ xs: '-120px', sm: '-80px', md: '-75px', lg: '-75px' }}
          left="50%"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          // flexWrap={{ xs: 'wrap', sm: 'no-wrap' }}
          zIndex={2}
          className={classes.footerArea}
          border={{ xs: 'solid 1px #000', sm: 'solid 1px #000', md: 'solid 1px #000' }}
        >
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            flexWrap={{ xs: 'wrap', sm: 'no-wrap' }}
            width={{ xs: '980px', sm: '980px', md: '980px' }}
          >
            {/*
            // この出力項目選択はなくして、タブの選択が出力の選択になるようにする 
            <Box mb={{ xs: '15px', sm: 0 }} display="flex" alignItems="center">
              <Box
                width={{ xs: '140px', sm: '140px', md: '140px' }}
                alignItems="center"
                lineHeight="20px"
                mx={1}
                color="white"
                textAlign="right"
              >
                <b>出力項目選択:</b>
              </Box>
              <MultiSelectBox
                itemList={Object.keys(CSV_OUTPUT_ITEMS).map(
                  (key) => CSV_OUTPUT_ITEMS[key as keyof typeof CSV_OUTPUT_ITEMS].name,
                )}
                selectedItems={outputItemNames}
                checkBoxOnClick={handleCheckBoxClick}
                style={{
                  width: 250,
                  height: 70,
                  helperTextColor: '#fff',
                }}
                helperText={osJudgement() === 'PC' ? '' : '※ PC以外の端末では単数選択のみ'}
              />
            </Box>
            */}
            <Box mr={{ xs: 0, sm: '15px' }} ml="20px" display="flex" alignItems="center">
              <Box
                width={{ xs: '140px', sm: '140px', md: '140px' }}
                alignItems="center"
                lineHeight="20px"
                mx={1}
                color="white"
                textAlign="right"
              >
                <b>サービス種類コード(要介護):</b>
              </Box>
              <Box
                mr={{ sx: 0, sm: '60px' }}
                display="flex"
                alignItems="center"
                width={{ xs: '250px', sm: '250px', md: '250px' }}
              >
                <GeneralPrimarySelectBox
                  selectedValue={serviceCode.id}
                  size="medium"
                  style={{ width: 250, height: 50 }}
                  selectBoxChanged={selectBoxChanged}
                >
                  {Object.keys(SERVICE_CODE).map((key) => (
                    <MenuItem key={key} value={SERVICE_CODE[key].id}>
                      {`${SERVICE_CODE[key].id}: ${SERVICE_CODE[key].name}`}
                    </MenuItem>
                  ))}
                </GeneralPrimarySelectBox>
              </Box>
            </Box>
            <Box mr={{ xs: 0, sm: '15px' }} ml="20px" display="flex" alignItems="center">
              <Box
                width={{ xs: '140px', sm: '140px', md: '140px' }}
                alignItems="center"
                lineHeight="20px"
                mx={1}
                color="white"
                textAlign="right"
              >
                <b>サービス種類コード(要支援):</b>
              </Box>
              <Box
                mr={{ sx: 0, sm: '60px' }}
                display="flex"
                alignItems="center"
                width={{ xs: '250px', sm: '250px', md: '250px' }}
              >
                <GeneralPrimarySelectBox
                  selectedValue={serviceCodeSupport.id}
                  size="medium"
                  style={{ width: 250, height: 50 }}
                  selectBoxChanged={selectCodeSupportBoxChanged}
                >
                  {Object.keys(SERVICE_CODE_SUPPORT).map((key) => (
                    <MenuItem key={key} value={SERVICE_CODE_SUPPORT[key].id}>
                      {`${SERVICE_CODE_SUPPORT[key].id}: ${SERVICE_CODE_SUPPORT[key].name}`}
                    </MenuItem>
                  ))}
                </GeneralPrimarySelectBox>
              </Box>
            </Box>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            flexWrap={{ xs: 'wrap', sm: 'no-wrap' }}
            flexDirection="row"
            width={{ xs: '980px', sm: '980px', md: '980px' }}
            mt="10px"
          >
            <Box mr={{ xs: 0, sm: '15px' }} display="flex" alignItems="center" width="100%">
              {/* ADL維持は、ADL維持等(BI)のタブを選択した場合のみ表示する */}
              {toggleAdl ? (
                <>
                  <Box
                    width={{ xs: '140px', sm: '140px', md: '140px' }}
                    alignItems="center"
                    lineHeight="20px"
                    mx={1}
                    color="white"
                    textAlign="right"
                  >
                    <b>ADL維持対象選択:</b>
                  </Box>
                  <Box mr={{ sx: 0, sm: '60px' }} display="flex" alignItems="center">
                    <GeneralPrimarySelectBox
                      selectedValue={adlCode.id}
                      size="medium"
                      style={{ width: 250, height: 50 }}
                      selectBoxChanged={selectADLBoxChanged}
                    >
                      {Object.keys(ADL_CODE).map((key) => (
                        <MenuItem key={key} value={ADL_CODE[key].id}>
                          {/*{`${ADL_CODE[key].id}: ${ADL_CODE[key].name}`}*/}
                          {`${ADL_CODE[key].name}`}
                        </MenuItem>
                      ))}
                    </GeneralPrimarySelectBox>
                  </Box>
                </>
              ) : null}
              <Box mr={{ xs: 0, sm: '15px' }} ml="20px" display="flex" alignItems="center" width="250px">
                <GeneralPrimaryButton
                  txt="LIFE CSV出力"
                  setCss={'width: 250px; height: 50px;'}
                  onClick={allUsersPrintButtonClickHandler}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className={classes.headerTitle}>
          <p>LIFE CSV出力</p>
        </Box>
        <Box component="section" m="30px 0 0 0" p="30px 25px 0">
          <h2 className={classes.ttlStyle01}>LIFE CSV出力対象者リスト</h2>
          <p>出力項目と対象利用者とサービス種類コードを選択し、LIFE CSV出力ボタンをクリックしてください</p>
          <p>・「生活機能」「興味関心」「科学的介護」「ADL維持」は、記録月のデータがある利用者名が出力されます</p>
          <p>・「個別機能訓練計画」は、作成月のデータがある利用者名が出力されます</p>
          <p>・同月に複数回、記録・作成がある場合は、月内の最新の記録が出力されます</p>
          <br />
          <Box display="flex" flexWrap="wrap" justifyContent="center">
            <Box mb={5}>
              <Box
                display="flex"
                flexWrap="wrap"
                justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
                displayPrint="none"
              >
                <Box alignItems="center" lineHeight="40px" mx={1}>
                  <b>出力項目選択:</b>
                </Box>
                <Box>
                  <TabSelector
                    selectedTab={getSelectedTabIndex()}
                    tabLabels={[
                      '個別機能訓練計画',
                      '生活機能',
                      '興味関心',
                      '科学的介護',
                      'ADL維持等(BI)',
                      '利用者情報',
                    ]}
                    onTabChange={handleTabChange}
                    customCss={'min-width: 840px;'}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            display="flex"
            justifyContent="center"
            flexWrap={{ xs: 'wrap', sm: 'no-wrap' }}
            data-test="monthly-header"
          >
            {!toggleUserInfo && (
              <Box m={{ xs: '0 0 10px', sm: '0 50px 25px' }} width={{ xs: '300px', sm: '350px' }}>
                <DateSelectorWithPicker
                  date={date}
                  className={''}
                  dateDisplayType={DATE_DISPLAY_TYPE.Month}
                  minusDate={minusMonthly}
                  plusDate={plusMonthly}
                  onDateChange={(newDate) => (onDateChange ? onDateChange(newDate) : undefined)}
                />
              </Box>
            )}
            <Box
              m={{ xs: '0 0 20px', sm: '0 0 25px' }}
              width="300px"
              fontSize="30px"
              textAlign="center"
              data-test="output-users-count"
            >
              <Box component="span" display="inline-block" m="0 0.2em 0 0.3em" height="16px" fontSize="16px">
                {'出力対象者　'}
              </Box>
              {checkedUsersCount}
              <Box component="span" display="inline-block" m="0 0.2em 0 0.3em" height="16px" fontSize="16px">
                {'　名'}
              </Box>
            </Box>
          </Box>
          {toggleUserInfo && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              p={2}
              mb={2}
              bgcolor="#fff3cd"
              border="1px solid #ffc107"
              borderRadius="4px"
              width="90%"
              mx="auto"
            >
              <Box color="#856404" fontSize="14px" fontWeight="bold">
                選択できない利用者がいる場合は、利用者基本情報ページの「保存」ボタンをクリックしてください。
              </Box>
            </Box>
          )}
          {isDataFetching ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={3}>
              <Box display="flex" flexDirection="column" alignItems="center">
                <Box mb={2}>
                  <CircularProgress />
                </Box>
                <Box>
                  <p>データを読み込み中...</p>
                </Box>
              </Box>
            </Box>
          ) : loaded && !_.isEmpty(displayUsers) ? (
            <UserList
              key={getSelectedTabIndex()} // タブが変更されるたびにコンポーネントを再マウントするためのキー
              users={displayUsers}
              invokedPath={location.pathname}
              currentMonth={date.format('YYYY-MM')}
              isCheckBox={true}
              isMemoColumn={false}
              isReportCheckColumn={false}
              onCheckChange={handleCheckChange}
              ref={(instance) => {
                refUsersList.current = instance;
              }}
              usersWithData={usersWithData}
              basicInfoUsers={basicInfoUsers}
              isUserInfoTab={toggleUserInfo}
            />
          ) : (
            <Box data-test="not-exist-comment">
              <p>該当月は出力対象者が存在しません。</p>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default LifeCSVOutput;
